import type { ComponentPropsWithoutRef, MouseEventHandler, ReactNode } from "react";
import { Link as RouterLink, useInRouterContext } from "react-router-dom";
import { cn } from "packages/dsaui/src/utils";

export const menuItemHoverClassName =
  "hover:bg-red-50 hover:text-dsa-red hover:no-underline focus-visible:ring-2 focus-visible:ring-dsa-red focus-visible:ring-offset-2 focus-visible:outline-none";

export const menuItemClassName = cn(
  "text-body-sm block w-full cursor-pointer border-0 bg-white text-left text-gray-700 no-underline visited:text-gray-700 disabled:cursor-not-allowed",
);

export const MenuLink = ({
  children,
  className,
  href,
  onClick,
  ...props
}: ComponentPropsWithoutRef<"a">) => {
  const isInRouterContext = useInRouterContext();
  const classes = cn(menuItemClassName, className);
  const isInternal =
    isInRouterContext && href?.startsWith("/") && !href.startsWith("//");
  const handleClick: MouseEventHandler<HTMLAnchorElement> = (event) => {
    onClick?.(event);

    if (!event.defaultPrevented) {
      event.currentTarget.closest<HTMLElement>("[popover]")?.hidePopover?.();
    }
  };

  if (isInternal && href) {
    return (
      <RouterLink className={classes} onClick={handleClick} to={href} {...props}>
        {children}
      </RouterLink>
    );
  }

  return (
    <a className={classes} href={href} onClick={handleClick} {...props}>
      {children}
    </a>
  );
};

export const MenuButton = ({
  className,
  type = "button",
  ...props
}: ComponentPropsWithoutRef<"button">) => (
  <button
    className={cn(
      menuItemClassName,
      "cursor-pointer appearance-none disabled:cursor-not-allowed",
      className,
    )}
    type={type}
    {...props}
  />
);

const actionListItemClassName = cn(
  menuItemHoverClassName,
  "flex items-center justify-between gap-4 rounded-md border border-gray-300 px-4 py-3 text-left transition-colors hover:border-dsa-red",
);

export const ActionListLink = ({
  children,
  className,
  icon,
  ...props
}: ComponentPropsWithoutRef<"a"> & { icon?: ReactNode }) => (
  <MenuLink className={cn(actionListItemClassName, className)} {...props}>
    <span>{children}</span>
    {icon}
  </MenuLink>
);

export const ActionListButton = ({
  children,
  className,
  icon,
  type = "button",
  ...props
}: ComponentPropsWithoutRef<"button"> & { icon?: ReactNode }) => (
  <MenuButton className={cn(actionListItemClassName, className)} type={type} {...props}>
    <span>{children}</span>
    {icon}
  </MenuButton>
);
